type CanonicalUrlInput = {
  explicitUrl?: string | null;
  itemPath?: string | null;
};

const hasProtocol = (value?: string | null) => !!value && /^(https?:)?\/\//i.test(value.trim());

const getBaseUrl = () => {
  if (process.env.PUBLIC_URL) {
    return process.env.PUBLIC_URL;
  }

  if (process.env.NEXT_PUBLIC_PUBLIC_URL) {
    return process.env.NEXT_PUBLIC_PUBLIC_URL;
  }

  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  return '';
};

/**
 * Builds a canonical URL using either the explicitly provided value or by combining the
 * current site's base URL with the Sitecore item path.
 */
export const getCanonicalUrl = ({ explicitUrl, itemPath }: CanonicalUrlInput): string => {
  const baseUrl = getBaseUrl();

  if (!hasProtocol(baseUrl)) {
    return '';
  }

  const normalizedBase = baseUrl.replace(/\/$/, '');

  if (explicitUrl) {
    const trimmedExplicit = explicitUrl.trim().replace(/#.*/, '');
    if (hasProtocol(trimmedExplicit)) {
      return trimmedExplicit;
    }

    try {
      return new URL(trimmedExplicit, `${normalizedBase}/`).toString();
    } catch (error) {
      console.warn('Failed to normalize explicit canonical URL', error);
      return trimmedExplicit;
    }
  }

  const sanitizedPath = (itemPath || '').trim().replace(/#.*/, '').replace(/\?.*/, '');

  const normalizedPath = sanitizedPath
    ? sanitizedPath.startsWith('/')
      ? sanitizedPath
      : `/${sanitizedPath}`
    : '';

  try {
    const url = new URL(normalizedPath || '/', `${normalizedBase}/`).toString();
    return url.endsWith('/') && normalizedPath && normalizedPath !== '/' ? url.slice(0, -1) : url;
  } catch (error) {
    console.warn('Failed to construct canonical URL', error);
    return '';
  }
};
